import * as React from "react";
import MaskedInput from "react-text-mask";
import { action, get, isObservable, isObservableProp } from "mobx";
import { ensureObservableProperty } from "@frui.ts/helpers";
import type { IBindingProps } from "@frui.ts/views";
import { BindingComponent, setValue } from "@frui.ts/views";
import { bound } from "@frui.ts/helpers";
import { Observer } from "mobx-react-lite";
import type { FormControlProps } from "react-bootstrap";
import { Form, InputGroup } from "react-bootstrap";
import type { CommonInputProps } from "@frui.ts/bootstrap/dist/controls/commonInputProps";
import type { InputProps } from "@frui.ts/bootstrap/dist/controls/input";
import { getValidationMessage } from "@frui.ts/validation";
import { IBankAccount } from "entities/types";
import { formatCzechAccountNumber, isCzechAccountNumberFormat } from "@edaz/utils/bankAccount";
import BankAccount from "../../entities/bankAccount";

export interface AccountInputWithAppendProps {
  bankCodeProperty: "bank" | "bankCode";
  accountNumberProperty: "accountNumber" | "bankAccountNumber";
  mode?: "object" | "property";
  append?: React.ReactNode;
  prepend?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  skipMask?: boolean;
  autoComplete?: boolean;
  inputType?: string;
  onInvalidValuePasted?: (pastedValue: string, event: any) => void;
}

export function accountInputMaskWithoutSlashAndDash(rawValue?: string) {
  return [
    /[0-9]/,
    /[0-9]/,
    /-|[0-9]|\//,
    /-|[0-9]|\//,
    /-|[0-9]|\//,
    /-|[0-9]|\//,
    /-|[0-9]|\//,
    /[0-9]|\//,
    /[0-9]|\//,
    /[0-9]|\//,
    /\//,
  ];
}

export function accountInputMaskWithoutDashWithSlash(rawValue: string) {
  const slashIndex = rawValue.indexOf("/");

  if (~slashIndex) {
    const maxLength = slashIndex + 5;
    return Array.from(Array(maxLength).keys()).map(index => {
      if (index < 2) {
        return /[0-9]/;
      } else if (index === slashIndex && slashIndex < 11) {
        return /[0-9]|\//;
      } else if (index >= 2 && index <= 7 && index < slashIndex) {
        return /-|[0-9]/;
      }

      return /[0-9]/;
    });
  }

  return [];
}

export function accountInputMaskWithDashWithoutSlash(rawValue: string) {
  const dashIndex = rawValue.indexOf("-");

  if (~dashIndex) {
    const maxLength = dashIndex + 12;
    return Array.from(Array(maxLength).keys()).map(index => {
      if (index < 2) {
        return /[0-9]/;
      } else if (index === dashIndex && dashIndex < 7) {
        return /-|[0-9]/;
      } else if (index === maxLength - 1) {
        return /\//;
      }

      return /[0-9]/;
    });
  }

  return [];
}

function couldBeFixedToCzechAccountNumberFormat(accountNumber: string) {
  return /^[0-9]{2,16}(\/[0-9]{4}){0,1}$/.test(accountNumber);
}

function addStr(str: string, index: number, stringToAdd: string) {
  return str.substring(0, index) + stringToAdd + str.substring(index, str.length);
}

export function fixCzechAccountNumber(accountNumber: string) {
  let fixedCzechAccountNumber;
  if (couldBeFixedToCzechAccountNumberFormat(accountNumber)) {
    if (accountNumber.length === 11 && !accountNumber.includes("/")) {
      fixedCzechAccountNumber = addStr(accountNumber, accountNumber.length - 9, "-");
    } else if (accountNumber.length > 11 && !accountNumber.includes("/")) {
      fixedCzechAccountNumber = addStr(accountNumber, accountNumber.length - 10, "-");
    } else if (accountNumber.length === 16 && accountNumber.includes("/")) {
      fixedCzechAccountNumber = addStr(accountNumber, accountNumber.length - 14, "-");
    } else if (accountNumber.length > 16 && accountNumber.includes("/")) {
      fixedCzechAccountNumber = addStr(accountNumber, accountNumber.length - 15, "-");
    } else {
      fixedCzechAccountNumber = accountNumber;
    }
  }

  return fixedCzechAccountNumber;
}

export function accountInputMaskWithDashAndSlash(rawValue: string) {
  const dashIndex = rawValue.indexOf("-");
  const slashIndex = rawValue.indexOf("/");

  if (~slashIndex && ~dashIndex) {
    const maxLength = slashIndex + 5;
    return Array.from(Array(maxLength).keys()).map(index => {
      if (index < 2) {
        return /[0-9]/;
      } else if (index === dashIndex && dashIndex < 7) {
        return /-|[0-9]/;
      } else if (index === slashIndex && slashIndex > dashIndex + 2 && slashIndex < 18) {
        return /[0-9]|\//;
      }

      return /[0-9]/;
    });
  }

  return [];
}

export default class AccountInput<TTarget> extends BindingComponent<
  TTarget,
  AccountInputWithAppendProps & FormControlProps & InputProps & CommonInputProps & IBindingProps<TTarget>
> {
  state = {
    pastedFixableValue: false,
  };

  static defaultProps = {
    bankCodeProperty: "bank",
    accountNumberProperty: "accountNumber",
  };

  render() {
    return <Observer render={this.renderInner} />;
  }

  protected get value() {
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string }; // the cast is a TS compiler workaround
    if (!target) {
      // tslint:disable-next-line: no-console
      console.warn("'target' prop has not been set");
      return undefined;
    }
    if (!property) {
      throw new Error("'property' prop has not been set");
    }
    if (!isObservable(target) || !isObservableProp(target, property)) {
      ensureObservableProperty(target, property, target[property]);
    }

    const value = get(target, property);
    const bankAccount = new BankAccount();
    if (value) {
      bankAccount.accountNumber = value[this.props.accountNumberProperty];
      bankAccount.bank = value[this.props.bankCodeProperty];
    }

    return formatCzechAccountNumber(bankAccount);
  }

  /**
   * Formát: 000000-1234567890/0600
   * - předčíslí – nepovinná část. Pokud je použitá, má alespoň 2 číslice, maximálně 6. Od následující části je oddělena pomlčkou
   * - základní část – musí obsahovat alespoň 2 číslice, maximálně 10
   * - kód banky – skládá se ze 4 číslic, oddělen lomítkem od základní části
   */
  @bound
  protected mask(rawValue?: string) {
    const trimmedValue = rawValue?.replace(new RegExp(`[^${["0-9", "/", "-"].join("")}]`, "g"), "");

    if (this.state.pastedFixableValue || this.props.skipMask) {
      return Array.from(Array((trimmedValue ?? "").length + 2).keys()).map(key => {
        return /.*/;
      });
    } else if (!trimmedValue?.includes("-")) {
      if (!trimmedValue?.includes("/")) {
        return accountInputMaskWithoutSlashAndDash(trimmedValue);
      } else {
        return accountInputMaskWithoutDashWithSlash(trimmedValue);
      }
    } else {
      if (!trimmedValue?.includes("/")) {
        return accountInputMaskWithDashWithoutSlash(trimmedValue);
      } else {
        return accountInputMaskWithDashAndSlash(trimmedValue);
      }
    }
  }

  @bound
  protected onPaste(event: any) {
    const pastedValue = event.clipboardData.getData("text").trim();

    if (couldBeFixedToCzechAccountNumberFormat(pastedValue)) {
      this.setState({ pastedFixableValue: true });
      return;
    }

    if (pastedValue && !isCzechAccountNumberFormat(pastedValue) && this.props.onInvalidValuePasted) {
      this.props.onInvalidValuePasted(pastedValue.replaceAll(" ", ""), event);
    }
  }

  @bound protected renderInner() {
    const { noValidation, errorMessage, append, prepend, placeholder, disabled, inputType, autoComplete } = this.inheritedProps;
    const { target, property } = this.props as { target: TTarget; property: keyof TTarget & string };
    const errors = [];
    const baseValidationError = (noValidation !== true && errorMessage) || getValidationMessage(target, property);
    const accountNumberError = getValidationMessage(target[property] as any, this.props.accountNumberProperty);
    const bankError = getValidationMessage(target[property] as any, this.props.bankCodeProperty);

    if (baseValidationError) {
      errors.push(baseValidationError);
    }

    if (accountNumberError) {
      errors.push(accountNumberError);
    }

    if (bankError) {
      errors.push(bankError);
    }

    const fullErrorMessage = errors.join(", ");

    return (
      <InputGroup>
        {prepend && (
          <InputGroup.Prepend>
            <InputGroup.Text>{prepend}</InputGroup.Text>
          </InputGroup.Prepend>
        )}
        <MaskedInput
          mask={this.mask}
          className={`form-control ${errors.length > 0 ? "is-invalid" : ""} ${this.props.className}`}
          guide={false}
          showMask={false}
          value={this.value}
          autoComplete={autoComplete ? undefined : "new-password"}
          autoFocus={this.props.autoFocus}
          onFocus={this.props.onFocus}
          onBlur={this.props.onBlur}
          onPaste={this.onPaste}
          onChange={this.handleValueChanged}
          placeholder={placeholder}
          disabled={disabled}
          type={inputType}
        />
        {append && (
          <InputGroup.Append>
            <InputGroup.Text>{append}</InputGroup.Text>
          </InputGroup.Append>
        )}
        {errors.length > 0 && <Form.Control.Feedback type="invalid">{fullErrorMessage}</Form.Control.Feedback>}
      </InputGroup>
    );
  }

  @bound protected handleValueChanged(e: React.FormEvent<any>) {
    const target = e.target as HTMLInputElement;
    let newValue = target.value;
    newValue = newValue.trim();
    const fixedValue = fixCzechAccountNumber(newValue);
    if (fixedValue !== undefined) {
      newValue = fixedValue;
    }

    const [accountNumber, bank] = newValue.split("/");

    const objectValue: IBankAccount = {
      accountNumber,
      bank,
    };

    this.setValue(objectValue);

    if (this.state.pastedFixableValue) {
      this.setState({ pastedFixableValue: false });
    }
    if (fixedValue?.includes("-") && !target.value.includes("-")) {
      target.value = fixedValue;
    }
  }

  @action.bound
  protected setValue(value: IBankAccount) {
    const { target, property, onValueChanged, mode } = this.props;

    if (mode === "object") {
      const bankAccount = new BankAccount();
      if (value.accountNumber !== undefined) {
        bankAccount.accountNumber = value.accountNumber;
      }
      if (value.bank !== undefined) {
        bankAccount.bank = value.bank;
      }

      setValue(target, property, bankAccount);
    } else {
      setValue((target as any)[property], this.props.accountNumberProperty, value.accountNumber);
      setValue((target as any)[property], this.props.bankCodeProperty, value.bank);
    }

    if (property && target) {
      onValueChanged?.(value, property, target);
    }
  }
}
